import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { trackEvent } from "@/lib/analytics";

/**
 * Handles the Google OAuth return from Emergent auth.
 * URL comes back as: {redirect_url}#session_id={session_id}
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshUser } = useApp();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/", { replace: true });
      return;
    }
    const session_id = match[1];

    (async () => {
      try {
        await api.post("/auth/session", { session_id });
        // The pathname at this point IS the "after" destination encoded
        // in the OAuth redirect URL (Login.jsx: origin + after). We honor
        // it so deep-link visitors (/organizza, /checkin, /dashboard …)
        // return to the page they came from.
        const intendedPath = window.location.pathname;
        // remove hash from URL
        window.history.replaceState({}, document.title, intendedPath);
        await refreshUser();
        // Fallback: if the OAuth round-trip somehow landed on root or on
        // /login (shouldn't happen with the current Login default, but
        // defend against stray entry points), send the user to the
        // checklist which doubles as onboarding for brand-new users
        // (DepartureProfileForm renders inline when the profile is empty).
        const dest =
          intendedPath && intendedPath !== "/" && intendedPath !== "/login"
            ? intendedPath
            : "/organizza";
        // Fire the analytics event and only navigate after Plausible has
        // sent (or a safety timeout fires). Without this, the XHR is often
        // aborted by the immediate router navigation and never reaches
        // plausible.io.
        trackEvent(
          "Login Completed",
          {},
          () => navigate(dest, { replace: true }),
        );
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, refreshUser]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="text-ink-soft font-sans">Signing you in…</div>
    </div>
  );
}
